import { useState, useCallback } from "react";
import {
	loadDefaultProductPrices,
	updateDefaultProductPrice,
	clearDefaultProductPrices,
	type DefaultProductPrices,
} from "../utils/componentSelections";

export interface UseDefaultPricesOptions {
	configBundleId: string;
}

export interface UseDefaultPricesReturn {
	defaultProductPrices: DefaultProductPrices;
	storeDefaultPrice: (
		productGroupId: string,
		defaultPrice: { net: number; gross: number },
		defaultProductId: string
	) => void;
	loadDefaultPrices: () => DefaultProductPrices;
	clearDefaults: () => void;
	setDefaultProductPrices: React.Dispatch<React.SetStateAction<DefaultProductPrices>>;
}

export function useDefaultPrices({
	configBundleId,
}: UseDefaultPricesOptions): UseDefaultPricesReturn {
	const [defaultProductPrices, setDefaultProductPrices] =
		useState<DefaultProductPrices>({});

	const storeDefaultPrice = useCallback(
		(
			productGroupId: string,
			defaultPrice: { net: number; gross: number },
			defaultProductId: string
		) => {
			const priceInfo = { price: defaultPrice, productId: defaultProductId };
			setDefaultProductPrices((prev) => ({
				...prev,
				[productGroupId]: priceInfo,
			}));

			if (configBundleId) {
				updateDefaultProductPrice(configBundleId, productGroupId, priceInfo);
			}
		},
		[configBundleId]
	);

	const loadDefaultPrices = useCallback((): DefaultProductPrices => {
		if (!configBundleId) return {};
		const loaded = loadDefaultProductPrices(configBundleId);
		setDefaultProductPrices(loaded);
		return loaded;
	}, [configBundleId]);

	const clearDefaults = useCallback(() => {
		if (configBundleId) {
			clearDefaultProductPrices(configBundleId);
		}
		setDefaultProductPrices({});
	}, [configBundleId]);

	return {
		defaultProductPrices,
		storeDefaultPrice,
		loadDefaultPrices,
		clearDefaults,
		setDefaultProductPrices,
	};
}
